import { LINES, type LineId } from '../data/map'
import type { TMSnapshot } from '../data/types'
import { useT } from '../../../i18n'

interface Props {
  snapshot: TMSnapshot
  /** Optional title override (defaults to "Income track"). */
  title?: string
}

/**
 * Income track — one row per metro line showing live income (yen).
 * Income climbs as players INVEST and START on a line and pays out
 * during scoring (per-stock + speculation payouts in
 * tm_finalize_scoring). The bar is normalised against the highest
 * line so the relative read is obvious at a glance.
 *
 * Sized for a phone column. On TV a larger version belongs in the
 * sidebar — todo.
 */
export function TokyoMetroIncomeTrack({ snapshot, title = 'Income track' }: Props) {
  const t = useT()
  const trains = snapshot.state.trains ?? []
  const incomeByLine = new Map<LineId, { income: number; running: boolean; speedTokens: number }>()
  for (const t of trains) {
    incomeByLine.set(t.line, {
      income: t.income ?? 0,
      running: t.running,
      speedTokens: t.speedTokens ?? 0,
    })
  }

  const max = Math.max(100, ...Array.from(incomeByLine.values()).map(v => v.income))

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted">
          {t(title)}
        </p>
        <p className="text-[10px] text-brand-muted">{t('Yen per line · pays out at game end')}</p>
      </div>

      <ul className="space-y-1.5">
        {LINES.map(line => {
          const v = incomeByLine.get(line.id) ?? { income: 0, running: false, speedTokens: 0 }
          const pct = (v.income / max) * 100
          return (
            <li key={line.id} className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: line.color }} />
              <span className="font-display font-bold text-xs w-4 shrink-0"
                    style={{ color: line.color }}>{line.id}</span>
              <span className="text-xs text-brand-text w-20 shrink-0 truncate">
                {line.name}
              </span>
              <div className="flex-1 h-3 rounded-full bg-brand-elevated overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: line.color,
                    opacity: v.running ? 1 : 0.5,
                  }}
                />
              </div>
              <span className="font-mono text-xs text-brand-text w-14 text-right tabular-nums">
                ¥{v.income}
              </span>
              {v.speedTokens > 0 && (
                <span className="text-[10px] text-brand-muted w-6 text-right">
                  ×{v.speedTokens + 1}
                </span>
              )}
            </li>
          )
        })}
      </ul>

      <p className="text-[10px] text-brand-muted mt-3">{t('Faded bars = line not yet started. Speed tokens widen the per-phase movement.')}</p>
    </div>
  )
}
